
/* eslint-disable max-lines */
import {
    PropType,
    computed, defineComponent,
    nextTick,
    ref,
} from 'vue';
import { toWritableComputedProps } from '@redviking/argonaut-core-ui/src/util/composables/writable-computed-prop';
import {
    SerialNumberFormatToken,
    SnfTokenDatetime,
    SnfTokenInput,
    SnfTokenSeq,
    legacyEncodingMap,
} from '@redviking/argonaut-util/src/serial-numbers';
import { useEditing } from 'src/components/EntityDetail';
import { encodingItemsMap, formatItemsMap } from './Datetime.token.vue';
import { messages } from 'src/i18n/i18n';
import { v4 as uuidv4 } from 'uuid';

const sequenceTokenEndErrorMessage = (start: number, end: number | null, calculatedMax: number) => {
    if (end !== null) {
        if (end < 0) {
            return 'End must be greater than 0';
        }
        if (end < start) {
            return `End must be >= ${start}`;
        }
        if (end > calculatedMax) {
            return `End must be <= ${calculatedMax}`;
        }
    }
    return null;
};

const sequenceTokenStartErrorMessage = (start: number, end: number | null, calculatedMax: number) => {
    if (start < 0) {
        return 'Start must be greater than 0';
    }
    if (sequenceTokenEndErrorMessage(start, end, calculatedMax) === null && start >= (end || calculatedMax)) {
        return `Start must be < ${end || calculatedMax}`;
    }
    return null;
};

const sequenceTokenIncrementErrorMessage = (increment: number, end: number | null, start: number, calculatedMax: number) => {
    if (increment < 1) {
        return 'Increment must be greater than 0';
    }
    if (sequenceTokenEndErrorMessage(start, end, calculatedMax) === null && sequenceTokenStartErrorMessage(start, end, calculatedMax) === null && increment > (end || calculatedMax) - start) {
        return `Increment must be <= ${(end || calculatedMax) - start}`;
    }
    return null;
};

const sequenceTokenLengthErrorMessage = (len: number) => {
    if (len < 1) {
        return 'Length must be greater than 0';
    }
    return null;
};

const sequenceTokenSequenceKeyTokenNameUniqueErrorMessage = (sequenceKeyTokens: SnfTokenSeq['sequenceKey']) => {
    const duplicateTokenNames: string[] = [];
    for (const sequenceKey of sequenceKeyTokens) {
        if (sequenceKeyTokens.some(t => t.name === sequenceKey.name && t.tokenId !== sequenceKey.tokenId) && !duplicateTokenNames.includes(sequenceKey.name)) {
            duplicateTokenNames.push(sequenceKey.name);
        }
    }
    if (duplicateTokenNames.length > 0) {
        return `Sequence key token names must be unique. The following token names were found more than once: ${duplicateTokenNames.join(', ')}`;
    }
    return null;
};

export const sequenceTokenConfigErrorMessage = (config: SnfTokenSeq) => {
    const endErrorMessage = sequenceTokenEndErrorMessage(config.start, config.end, (legacyEncodingMap[config.encoding].length ** config.len) - 1);
    if (endErrorMessage) {
        return endErrorMessage;
    }
    const startErrorMessage = sequenceTokenStartErrorMessage(config.start, config.end, (legacyEncodingMap[config.encoding].length ** config.len) - 1);
    if (startErrorMessage) {
        return startErrorMessage;
    }

    const incrementErrorMessage = sequenceTokenIncrementErrorMessage(config.inc, config.end, config.start, (legacyEncodingMap[config.encoding].length ** config.len) - 1);
    if (incrementErrorMessage) {
        return incrementErrorMessage;
    }

    const lengthErrorMessage = sequenceTokenLengthErrorMessage(config.len);
    if (lengthErrorMessage) {
        return lengthErrorMessage;
    }

    const sequenceKeyTokenNameUniqueErrorMessage = sequenceTokenSequenceKeyTokenNameUniqueErrorMessage(config.sequenceKey);
    if (sequenceKeyTokenNameUniqueErrorMessage) {
        return sequenceKeyTokenNameUniqueErrorMessage;
    }

    return null;
};

export default defineComponent({
    name: 'SequenceToken',
    props: {
        readonly: { type: Boolean, required: true },
        value: { type: Object as PropType<SnfTokenSeq>, required: true },
        tokens: { type: Array as PropType<SerialNumberFormatToken[]>, required: true },
    },
    setup (props, { emit }) {
        const activeSequenceKeyTokenIdx = ref(-1);
        const showTokenMenu = ref(false);
        const editing = useEditing.useInject().ref;
        const menuPos = ref({
            x: 0,
            y: 0,
        });
        const token = computed({
            get: () => props.value,
            set: v => emit('input', v),
        });

        const {
            len,
            end,
            inc,
            start,
            encoding,
            sequenceKey,
        } = toWritableComputedProps(token);

        const activeSequenceKeyToken = computed<SnfTokenSeq['sequenceKey'][number] | null>({
            get: () => sequenceKey.value[activeSequenceKeyTokenIdx.value] || null,
            set: v => {
                if (!v) {
                    return;
                }
                sequenceKey.value = sequenceKey.value.map(t => {
                    if (t.tokenId === v.tokenId) {
                        return v;
                    }
                    return t;
                });
            },
        });

        const calculatedMax = computed(() => (legacyEncodingMap[encoding.value].length ** len.value) - 1);
        const endErrorMsg = computed(() => sequenceTokenEndErrorMessage(start.value, end.value, calculatedMax.value));
        const startErrorMsg = computed(() => sequenceTokenStartErrorMessage(start.value, end.value, calculatedMax.value));

        return {
            end,
            inc,
            len,
            token,
            start,
            editing,
            menuPos,
            encoding,
            sequenceKey,
            endErrorMsg,
            calculatedMax,
            showTokenMenu,
            startErrorMsg,
            formatItemsMap,
            encodingItemsMap,
            activeSequenceKeyToken,
            activeSequenceKeyTokenIdx,
            lengthErrorMsg: computed(() => sequenceTokenLengthErrorMessage(len.value)),
            nonUniqueName: computed(() => sequenceTokenSequenceKeyTokenNameUniqueErrorMessage(sequenceKey.value)),
            incrementErrorMsg: computed(() => sequenceTokenIncrementErrorMessage(inc.value, end.value, start.value, calculatedMax.value)),
            deleteToken (tokenId: string) {
                activeSequenceKeyTokenIdx.value = -1;
                sequenceKey.value = sequenceKey.value.filter(t => t.tokenId !== tokenId);
            },
            tokenTypeMapItems: computed(() => {
                const inputTokens = props.tokens.filter((t): t is SnfTokenInput => t.type === 'input');
                const ret: ({
                    text: string;
                    desc: string;
                    disabled: boolean;
                    type: SnfTokenDatetime['type'];
                } | {
                    text: string;
                    desc: string;
                    tokenId: string;
                    disabled: boolean;
                    type: SnfTokenInput['type'];
                })[] = [
                    {
                        disabled: false,
                        type: 'datetime',
                        text: messages.serialNum.serialNumber.token.types.datetime.text,
                        desc: messages.serialNum.serialNumber.token.types.datetime.desc,
                    },
                ];
                if (inputTokens.length) {
                    ret.push(...inputTokens.map<{
                        text: string;
                        desc: string;
                        tokenId: string;
                        disabled: boolean;
                        type: SnfTokenInput['type'];
                    }>(t => ({
                        text: t.name,
                        type: 'input',
                        tokenId: t.id,
                        disabled: false,
                        desc: 'User defined input token',
                    })));
                } else {
                    ret.push({
                        tokenId: '',
                        type: 'input',
                        disabled: true,
                        text: 'Add an input token to use it here',
                        desc: 'No input tokens have been defined yet',
                    });
                }
                return ret;
            }),
            openTokenMenu (e: PointerEvent) {
                e.preventDefault();
                showTokenMenu.value = false;
                menuPos.value = {
                    x: e.clientX,
                    y: e.clientY,
                };
                nextTick(() => {
                    showTokenMenu.value = true;
                });
            },
            addSequenceKeyToken (payload: {
                name: string;
                tokenId: string;
                type: SnfTokenInput['type'];
            } | {
                type: SnfTokenDatetime['type']
            }) {
                if (payload.type === 'datetime') {
                    sequenceKey.value = [
                        ...sequenceKey.value,
                        {
                            type: 'datetime',
                            format: 'julian',
                            tokenId: uuidv4(),
                            name: 'Sequence Key Token',
                        },
                    ];
                } else if (payload.type === 'input') {
                    sequenceKey.value = [
                        ...sequenceKey.value,
                        {
                            type: 'input',
                            name: payload.name,
                            tokenId: payload.tokenId,
                        },
                    ];
                } else {
                    throw new Error(`Unknown token type: ${payload}`);
                }
                // using next tick to help with reactivity on counting length with a ref
                nextTick(() => {
                    activeSequenceKeyTokenIdx.value = sequenceKey.value.length - 1;
                });
            },
        };
    },
});
